import { Check, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { burst } from '../lib/fx';
import LivingEmoji from './living/LivingEmoji';

const PRIO = { high: 'bg-brand-terracotta/15 text-brand-terracotta border-brand-terracotta/30', medium: 'bg-brand-ochre/15 text-brand-ochre border-brand-ochre/30', low: 'bg-brand-sage/20 text-brand-green border-brand-sage/30' };

export default function TaskCard({ task, onChanged, compact = false }) {
    const toggle = async (e) => {
        // Completing a task deserves a little celebration
        if (!task.done && e?.currentTarget) burst(e.currentTarget, 'confetti');
        try {
            await api.patch(`/tasks/${task.id}`, { done: !task.done });
            onChanged?.();
        } catch {
            toast.error('Fejl');
        }
    };
    const del = async (e) => {
        e.stopPropagation();
        try {
            await api.delete(`/tasks/${task.id}`);
            onChanged?.();
        } catch {}
    };

    if (compact) {
        return (
            <div
                data-testid={`task-card-${task.id}`}
                onClick={toggle}
                className={`snap-start min-w-[180px] max-w-[180px] flex flex-col bg-brand-card border border-brand-border rounded-2xl p-3.5 cursor-pointer transition-all duration-300 hover:border-brand-sage ${task.done ? 'opacity-60' : ''}`}
            >
                <div className="flex items-start justify-between">
                    <LivingEmoji kind="bob" seed={task.id} className="text-2xl">{task.icon || '🌿'}</LivingEmoji>
                    <button
                        data-testid={`btn-toggle-task-${task.id}`}
                        onClick={(e) => { e.stopPropagation(); toggle(e); }}
                        aria-label="toggle"
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${task.done ? 'bg-brand-green border-brand-green text-white' : 'border-brand-border bg-brand-bg2'}`}
                    >
                        {task.done && <Check size={13} strokeWidth={3} />}
                    </button>
                </div>
                <h4 className={`font-medium text-sm text-brand-ink mt-2 line-clamp-2 leading-snug ${task.done ? 'line-through' : ''}`}>{task.title}</h4>
                <div className="mt-auto pt-2 flex items-center gap-1.5 flex-wrap">
                    <span className="text-[9px] uppercase tracking-widest font-semibold text-brand-ink3">{task.time_of_day}</span>
                    <span className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded-full border ${PRIO[task.priority] || PRIO.medium}`}>{task.priority}</span>
                </div>
            </div>
        );
    }

    return (
        <div
            data-testid={`task-card-${task.id}`}
            onClick={toggle}
            className={`group flex items-start gap-3 bg-brand-card border border-brand-border rounded-2xl p-4 cursor-pointer transition-all duration-300 hover:border-brand-sage ${task.done ? 'opacity-60' : ''}`}
        >
            <button
                data-testid={`btn-toggle-task-${task.id}`}
                onClick={(e) => { e.stopPropagation(); toggle(e); }}
                aria-label="toggle"
                className={`mt-0.5 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                    task.done ? 'bg-brand-green border-brand-green text-white' : 'border-brand-border bg-brand-bg2'
                }`}
            >
                {task.done && <Check size={13} strokeWidth={3} />}
            </button>

            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <LivingEmoji kind="bob" seed={task.id} className="text-lg">{task.icon || '🌿'}</LivingEmoji>
                    <h4 className={`font-medium text-brand-ink truncate ${task.done ? 'line-through' : ''}`}>{task.title}</h4>
                </div>
                {task.description && (
                    <p className="text-sm text-brand-ink2 mt-1 leading-relaxed">{task.description}</p>
                )}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">
                        {task.time_of_day}
                    </span>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-full border ${PRIO[task.priority] || PRIO.medium}`}>
                        {task.priority}
                    </span>
                </div>
            </div>

            <button
                data-testid={`btn-delete-task-${task.id}`}
                onClick={del}
                className="text-brand-ink3 hover:text-brand-terracotta transition-colors p-1"
                aria-label="delete"
            >
                <Trash2 size={15} strokeWidth={1.6} />
            </button>
        </div>
    );
}
